/**
 * When this company's financial year starts.
 *
 * Every window on these pages was computed from April, hardcoded in three
 * places. A company reporting July to June was therefore asked for a year by
 * name and given a date range three months away from it — the request carries
 * both, so the two disagreed and the charts came back empty or showing the
 * wrong quarter.
 *
 * The rest of the product already knows the answer: `starting_month` on the
 * signed-in user, which Settings reads for its own period arithmetic. This is
 * the same value, read the same way, for the one module that assumed instead.
 */

/** Month a financial year starts in when the company has not said (April). */
export const DEFAULT_FY_START_MONTH = 3;

/** Months in a year, for turning a stored month into a moment one. */
const MONTHS = 12;

/**
 * Reads the signed-in user without throwing on absent or damaged storage.
 * @returns {object|null} The stored user, or null when there is none to read.
 */
const currentUser = () => {
  try {
    const raw = localStorage.getItem('currentUser');
    return raw == null ? null : JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * The month the company's financial year opens in, zero-based.
 *
 * `starting_month` is stored one-based — Settings subtracts one before using
 * it — while moment counts months from zero. April is 4 in storage and 3 here,
 * and reversing that shifts every window by a month without an April company
 * ever noticing.
 *
 * @returns {number} A month index moment can use, 0 through 11.
 */
export const fyStartMonth = () => {
  const stored = Number(currentUser()?.starting_month);

  if (!Number.isInteger(stored) || stored < 1 || stored > MONTHS) {
    return DEFAULT_FY_START_MONTH;
  }

  return stored - 1;
};
